import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from '@root/components/modal';
import Toast from '@root/components/toast';
import { getQueryString } from '@root/shared';
import { IUnbindDevice } from '@root/types/device';
import { useSubscriptionDetailData } from '../subscription/useDetail';
import useCreateOrder from './useCreateOrder';
import usePackages from './usePackages';

const useSelectDevices = (): {
  isChangePlan: boolean;
  selected: string[];
  maxDevNum?: number;
  paying?: boolean;
  loading: number;
  expandDeviceList: IUnbindDevice[];
  onSelected: (vs: any[]) => void;
  onClickPay: () => void;
  getPackageDetail: () => void;
} => {
  const navigate = useNavigate();
  const { paying, onCreateOrder } = useCreateOrder();
  const changePackageId = getQueryString('changePackageId') || '';
  const {
    stateId: stateDetailId,
    detail,
    loading: loadingDetail,
    getDetail,
  } = useSubscriptionDetailData(changePackageId);
  const { t } = useTranslation();
  const { packageId } = useParams();
  const { selectedPackage } = usePackages();
  const [selected, setSelected] = useState<string[]>([]);

  const maxDevNum = selectedPackage?.maxDevNum || 0;

  const expandDeviceList = useMemo(() => {
    // 升降级才需要扩展设备列表
    // 将已在当前套餐的设备
    if (
      changePackageId &&
      changePackageId === stateDetailId &&
      detail?.devList?.length
    ) {
      return detail.devList;
    }
    return [];
  }, [detail, changePackageId, stateDetailId]);

  useEffect(() => {
    if (expandDeviceList.length) {
      setSelected((v) => {
        const vs = [...v];

        expandDeviceList.forEach((i) => {
          if (!vs.includes(i.deviceId)) {
            vs.push(i.deviceId);
          }
        }, []);

        return vs;
      });
    }
  }, [expandDeviceList]);

  const onSelected = useCallback((vs: any[]) => {
    setSelected(vs);
  }, []);

  const onClickPay = useCallback(() => {
    if (!selectedPackage) {
      navigate(-1);
      return;
    }

    if (selected.length > maxDevNum) {
      Toast.info(t('service_max', { maxDevNum }));
      return;
    }

    if (!selected.length) {
      Modal.confirm({
        footerColumn: true,
        content: t('plan_detail_unselect_tips'),
        confirm: t('unselected_continue'),
        cancel: t('cancel'),
        onConfirm: () => {
          onCreateOrder({ packageInfo: selectedPackage });
        },
      });
      return;
    }

    onCreateOrder({ packageInfo: selectedPackage, deviceIds: selected });
  }, [selectedPackage, selected, maxDevNum, onCreateOrder, navigate, t]);

  useEffect(() => {
    if (selectedPackage?.goodsId !== packageId) {
      navigate(-1);
    }
  }, [navigate, packageId, selectedPackage]);

  const loading = useMemo(() => {
    if (changePackageId) {
      return loadingDetail;
    }
    return 1;
  }, [loadingDetail, changePackageId]);

  useEffect(() => {
    return () => {
      Toast.destroy();
      Modal.close();
    };
  }, []);

  return {
    isChangePlan: !!changePackageId,
    selected,
    maxDevNum,
    paying,
    loading,
    expandDeviceList,
    onSelected,
    onClickPay,
    getPackageDetail: getDetail,
  };
};

export default useSelectDevices;
